/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { keyframes } from "@mui/system";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Icons
import PeopleIcon from "@mui/icons-material/People";
import PostAddIcon from "@mui/icons-material/PostAdd";
import CategoryIcon from "@mui/icons-material/Category";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentIcon from "@mui/icons-material/Payment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SellIcon from "@mui/icons-material/Sell";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

import BASE_URL from "BASE_URL";
import Projects from "./components/Projects";
import OrdersOverview from "./components/OrdersOverview";

// Animations
const fadeInUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress color="info" />
        </MDBox>
      </DashboardLayout>
    );
  }

  const stats = [
    {
      color: "info",
      icon: <PeopleIcon color="white" fontSize="small" />,
      title: "Total Users",
      count: dashboardData?.totalUsers || 0,
      percentage: { color: "success", amount: "", label: "Active users" },
    },
    {
      color: "success",
      icon: <PostAddIcon color="white" fontSize="small" />,
      title: "Total Posts",
      count: dashboardData?.totalPosts || 0,
      percentage: { color: "success", amount: "", label: "All posts" },
    },
    {
      color: "warning",
      icon: <CategoryIcon color="white" fontSize="small" />,
      title: "Main Categories",
      count: dashboardData?.totalCategories || 0,
      percentage: { color: "success", amount: "", label: "Active categories" },
    },
    {
      color: "primary",
      icon: <SubdirectoryArrowRightIcon color="white" fontSize="small" />,
      title: "Sub Categories",
      count: dashboardData?.totalSubCategories || 0,
      percentage: { color: "success", amount: "", label: "Total subcategories" },
    },
    {
      color: "dark",
      icon: <SubdirectoryArrowRightIcon color="white" fontSize="small" />,
      title: "Child Categories",
      count: dashboardData?.totalChildCategories || 0,
      percentage: { color: "success", amount: "", label: "Total child categories" },
    },
    {
      color: "primary",
      icon: <PaymentIcon color="white" fontSize="small" />,
      title: "Total Revenue",
      count: dashboardData?.totalRevenue || 0.0,
      percentage: { color: "success", amount: "", label: "Total revenue" },
    },
  ];

  const getCardVariant = (card) => {
    const { color, title } = card;
    // Handle duplicate 'primary' by looking at the title
    if (color === "primary") {
      return title === "Main Categories" ? "royal" : "gold";
    }
    // Map other colors directly
    const mapping = {
      info: "ocean",
      success: "emerald",
      warning: "sunset",
      dark: "fire",
    };
    return mapping[color] || "royal";
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
        <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <MDBox mb={1}>
                <ComplexStatisticsCard
                  color={getCardVariant(stat)}
                  icon={stat.icon}
                  title={stat.title}
                  count={stat.count}
                  percentage={stat.percentage}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>

        <MDBox mt={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={8}>
              <Projects posts={dashboardData?.top10Posts || []} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <OrdersOverview payments={dashboardData?.top10Payments || []} />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default Dashboard;
